import { Button, Stack, Typography, Link as MuiLink, Alert } from "@mui/material";
import { Link, useNavigation } from "react-router-dom";
import FrameBox from "../../ui/containers/FrameBox";
import { Box, styled } from "@mui/system";
import CenteredContentBox from "../../ui/containers/CenteredContentBox";
import { ReactComponent as Separator }
	from "../../assets/decorative/separator-hor.svg";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import LockIcon from '@mui/icons-material/Lock';
import LockClockIcon from '@mui/icons-material/LockClock';
import { useDispatch } from "react-redux";
import { openScanner, openListSetup, openTariffy } from "../../store/utils-slice";
import useGameSettings from "../../hooks/use-game-settings";
import Loading from "../../ui/Loading";
import RippedBox from "../../ui/containers/RippedBox";
import AppPromo from "../AppPromo";
import FrameDialog from "../../ui/containers/FrameDialog";
import FrameHeader from "../../ui/containers/FrameHeader";
import FrameDialogContent from "../../ui/containers/FrameDialogContent";
import { useContext, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import UserCtx from "../../store/UserCtx";
import PromptDialog from "../../ui/PromptDialog";
import { login } from "../../lib/api";

const GameLogo = styled("img")(({ theme }) => ({
	height: "90px",
	[theme.breakpoints.only("xs")]: {
		height: "70px",
	},
}));


const Line = styled(Separator)(({ theme }) => ({
	height: "40px",
	fill: "currentColor",
	padding: theme.spacing(0, 0, 3, 0),
}));


function MainGameMenu({ game }) {
	const { slug, name } = game;
	const { getGameSettings } = useGameSettings();
	const { logo } = getGameSettings(slug);
	const dispatch = useDispatch();
	const { state } = useNavigation();
	const [newsOpen, setNewsOpen] = useState(false);
	const { currentUser, onCurrentUserChange } = useContext(UserCtx);
	const [openLockedMessage, setOpenLockedMessage] = useState(false);
	const [openTrialMessage, setOpenTrialMessage] = useState(false);
	const [openLoginMessage, setOpenLoginMessage] = useState(false);

	function handleNewList() {
		dispatch(openListSetup());
	}

	function handleScannerOpen() {
		dispatch(openScanner());
	}

	function handleLockedMessageClose() {
		setOpenLockedMessage(false);
	}

	function handleLoginMessageClose() {
		setOpenLoginMessage(false);
	}

	function handleLoginMessageOpen() {
		setOpenLoginMessage(true);
	}

	function handleLockedMessageOpen() {
		setOpenLockedMessage(true);
	}

	function handleTrialMessageClose() {
		setOpenTrialMessage(false);
	}

	function handleTrialMessageOpen() {
		setOpenTrialMessage(true);
	}

	function handleUnlock() {
		handleTrialMessageClose();
		handleLockedMessageClose();
		dispatch(openTariffy());
	}

	async function handleLogin() {
		handleLoginMessageClose();
		const {user, data} = await login();
		onCurrentUserChange(user, data);
	}

	if (state === "loading") {
		return (
			<CenteredContentBox>
				<Loading />
			</CenteredContentBox>
		);
	}

	return (
		<>
			<CenteredContentBox>
				<Stack spacing={2} alignItems="center">
					<Button
						variant="contained"
						size="large"
						onClick={() => setNewsOpen(true)}
						startIcon={<NewReleasesIcon />}
						sx={{width: 230, marginBottom: 2}}
					>
						News
					</Button>
					<FrameBox
						frameStyle="frame1"
						bgcolor="darkBgr.main"
						color="darkBgr.contrastText"
					>
						<Stack spacing={2} m={{ xs: 5, sm: 9 }}>
							<GameLogo src={logo} alt={name} />
							<Line />
							<Button
								component={Link}
								to="factions"
								variant="contained"
								size="large"
							>
								Browse Factions
							</Button>
							{currentUser?.list_building === "unlocked" && (
								<Button variant="contained" size="large" onClick={handleNewList}>
									Prepare for War
								</Button>
							)}
							{currentUser?.list_building === "trial" && (
								<Button variant="contained" size="large" onClick={handleTrialMessageOpen} startIcon={<LockClockIcon />}>
									Prepare for War
								</Button>
							)}
							{currentUser?.list_building === "locked" && (
								<Button variant="contained" size="large" onClick={handleLockedMessageOpen} startIcon={<LockIcon />}>
									Prepare for War
								</Button>
							)}
							{!currentUser && (
								<Button variant="contained" size="large" onClick={handleLoginMessageOpen}>
									Login to Go to War
								</Button>
							)}
							{/* {!currentUser && (
								<Button variant="contained" size="large" onClick={handleNewList}>
									Prepare for War
								</Button>
							)} */}
							<Button
								variant="contained"
								size="large"
								onClick={handleScannerOpen}
								startIcon={<QrCodeScannerIcon />}
							>
								Scan Card
							</Button>
						</Stack>
					</FrameBox>
					<Box pt={4}>
						<AppPromo />
					</Box>
				</Stack>
			</CenteredContentBox>
			<FrameDialog
				open={newsOpen}
				responsive
				onClose={() => setNewsOpen(false)}
				actions={[
					{
						id: "Cancel",
						icon: CloseIcon,
						handler: () => setNewsOpen(false),
					}
				]}
			>
			<FrameHeader title="News" />
				<FrameDialogContent>
					<Typography variant="h5" gutterBottom textAlign="center">
						<strong>Post-AdeptiCon TLAOK update is now live!</strong>
					</Typography>
					<Typography gutterBottom textAlign="center">
						Check out all the latest rules updates and FAQs <MuiLink href="https://www.para-bellum.com/rules-and-faq/" target="_blank" rel="noopener noreferrer">here</MuiLink>.
					</Typography>
					<Typography gutterBottom textAlign="center">
						And don't miss the new Weaver Courts pre-orders, now available <MuiLink href="https://eshop.para-bellum.com/product-category/conquest/new-upcoming/pre-orders/" target="_blank" rel="noopener noreferrer">on our e-shop</MuiLink>.
					</Typography>
				</FrameDialogContent>
			</FrameDialog>
			<PromptDialog
				open={openLockedMessage}
				onClose={handleLockedMessageClose}
				title="Account locked"
				content={"While your account is locked, certain features of the app are not accessible. Find the activation code in one of our product boxes and hit the Unlock button below."}
				actions={[
					{ label: "Cancel", handler: handleLockedMessageClose },
					{ label: "Unlock", handler: handleUnlock },
				]}
			/>
			<PromptDialog
				open={openTrialMessage}
				onClose={handleTrialMessageClose}
				title="Trial period"
				content={"Your account will soon be locked and certain features of the app will not be accessible. Find the activation code in one of our product boxes and hit the Unlock button below to make sure you don't miss out."}
				actions={[
					{ label: "Start a new list", handler: () => {
						handleTrialMessageClose();
						handleNewList();
					}},
					{ label: "Unlock", handler: handleUnlock },
				]}
			/>
			<PromptDialog
				open={openLoginMessage}
				onClose={handleLoginMessageClose}
				title="Login"
				content={(
					<Typography>
						Please login to access list building. Don't have an Account? Create one <MuiLink href="https://living.para-bellum.com/signup" target="_blank" rel="noopener noreferrer">here</MuiLink>
					</Typography>
				)}
				actions={[
					{ label: "Cancel", handler: handleLoginMessageClose },
					{ label: "Login", handler: handleLogin },
				]}
			/>
		</>
	);
}


export default MainGameMenu;